<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const emit = defineEmits(['stateChange']);
const { t } = useI18n();
const route = useRoute();
const requests = ref([]);
const isLoading = ref(false);
const loadError = ref(false);
const accountId = computed(() => Number(route.params.accountId));
let fetchSequence = 0;

const emitState = () => {
  emit('stateChange', {
    count: requests.value.length,
    loading: isLoading.value,
    error: loadError.value,
  });
};

const formatDate = value => {
  if (!value) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusKeys = {
  pending: 'INBOX.REQUESTS.STATUS_PENDING',
  approved: 'INBOX.REQUESTS.STATUS_APPROVED',
  rejected: 'INBOX.REQUESTS.STATUS_REJECTED',
};

const statusClasses = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

const fetchMyRequests = async () => {
  fetchSequence += 1;
  const currentSequence = fetchSequence;
  isLoading.value = true;
  emitState();

  try {
    const [carteirizacao, atendimento] = await Promise.all([
      ConversationTransferRequestsAPI.mine(),
      AssignmentTransferRequestsAPI.mine(),
    ]);
    if (currentSequence !== fetchSequence) return;

    requests.value = [
      ...(carteirizacao?.data?.payload || []),
      ...(atendimento?.data?.payload || []),
    ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    loadError.value = false;
  } catch {
    if (currentSequence === fetchSequence) loadError.value = true;
  } finally {
    if (currentSequence === fetchSequence) {
      isLoading.value = false;
      emitState();
    }
  }
};

const refreshForRequestUpdate = payload => {
  if (Number(payload?.account_id) === accountId.value) fetchMyRequests();
};

onMounted(() => {
  emitter.on(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    refreshForRequestUpdate
  );
  emitter.on(
    BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED,
    refreshForRequestUpdate
  );
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchMyRequests);
  window.addEventListener('focus', fetchMyRequests);
  fetchMyRequests();
});

onBeforeUnmount(() => {
  emitter.off(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    refreshForRequestUpdate
  );
  emitter.off(
    BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED,
    refreshForRequestUpdate
  );
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchMyRequests);
  window.removeEventListener('focus', fetchMyRequests);
});
</script>

<template>
  <section
    v-if="requests.length || loadError"
    data-testid="my-transfer-requests-panel"
    class="shrink-0 border-b border-n-weak bg-n-surface-2 px-4 py-4 md:px-5"
  >
    <div class="mb-3 flex items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2.5">
        <span
          class="i-lucide-send size-8 flex-shrink-0 rounded-lg bg-n-alpha-2 p-1.5 text-n-slate-11"
          aria-hidden="true"
        />
        <div class="min-w-0">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('INBOX.REQUESTS.MY_REQUESTS') }}
          </h2>
          <p class="text-xs text-n-slate-9">
            {{ t('INBOX.REQUESTS.MY_REQUESTS_DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-n-weak bg-n-surface-1 px-2.5 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1 disabled:opacity-60"
        :disabled="isLoading"
        @click="fetchMyRequests"
      >
        <span class="i-lucide-refresh-cw size-3.5" aria-hidden="true" />
        {{ t('INBOX.REQUESTS.REFRESH') }}
      </button>
    </div>

    <p
      v-if="loadError"
      role="alert"
      class="mb-2 rounded-lg border border-n-ruby-5 bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
    >
      {{ t('INBOX.REQUESTS.LOAD_ERROR') }}
    </p>

    <div class="max-h-[35vh] space-y-2.5 overflow-y-auto pr-1">
      <article
        v-for="request in requests"
        :key="`${request.kind}-${request.id}`"
        class="rounded-xl border border-n-weak bg-n-surface-1 p-3 shadow-sm"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold text-n-slate-12">
              {{
                t(
                  request.kind === 'atendimento'
                    ? 'INBOX.REQUESTS.ATTENDANCE'
                    : 'INBOX.REQUESTS.PORTFOLIO'
                )
              }}
            </p>
            <p class="truncate text-xs text-n-slate-10">
              #{{ request.conversation_id }}
              <span v-if="request.contact_name">
                · {{ request.contact_name }}
              </span>
            </p>
          </div>
          <span
            class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
            :class="statusClasses[request.status] || statusClasses.pending"
          >
            {{ t(statusKeys[request.status] || statusKeys.pending) }}
          </span>
        </div>

        <p class="mt-2 text-xs text-n-slate-10">
          {{
            t('INBOX.REQUESTS.TARGET', { name: request.target_assignee?.name })
          }}
        </p>
        <p class="mt-1 text-xs text-n-slate-9">
          {{
            t('INBOX.REQUESTS.REQUESTED_AT', {
              date: formatDate(request.created_at),
            })
          }}
        </p>
        <p v-if="request.decided_at" class="text-xs text-n-slate-9">
          {{
            t('INBOX.REQUESTS.DECIDED_AT', {
              date: formatDate(request.decided_at),
            })
          }}
        </p>
        <p v-if="request.decided_by?.name" class="text-xs text-n-slate-9">
          {{
            t('INBOX.REQUESTS.DECIDED_BY', { name: request.decided_by.name })
          }}
        </p>
        <p
          class="mt-3 rounded-lg bg-n-surface-2 px-3 py-2 break-words text-xs leading-relaxed text-n-slate-11"
        >
          <span class="font-semibold">
            {{ t('INBOX.REQUESTS.JUSTIFICATION') }}:
          </span>
          {{ request.justification }}
        </p>
      </article>
    </div>
  </section>
</template>
